import React, { useCallback } from 'react';
import { StyleSheet } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { NavigationContainer } from '@react-navigation/native';
import StartupScreen from '../screens/StartupScreen';
import { RootNavigator, AuthStack } from './RootNavigator';
import { navigationRef } from '../utils/navigationRef';

import AwesomeAlert from '../lib/awesome-alert/AwesomeAlert';
import { closeAlert } from '../store/actions/alert';
import { Color, CommonProperties } from '../styles';
import { useTranslation } from 'react-i18next';
import { useReduxDevToolsExtension } from '@react-navigation/devtools';
import { REBOOK_ENABLED, EVENTS_ENABLED, AA_ENABLED } from '../config/env';
import { selectRebookPassengers } from '../store/selectors/mytrips.selector';
import LoadingIndicator from '../components/modals/LoadingIndicator';

export const AppNavigator = () => {
  const hasToken = useSelector((state) => !!state.auth.tokenData);
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const alertState = useSelector((state) => state.alert);
  const passengers = useSelector(selectRebookPassengers);
  const closeAlertCallback = useCallback(() => {
    if (alertState.options?.onCancelPressed) {
      alertState.options.onCancelPressed();
    }
    dispatch(closeAlert()), [dispatch];
  });
  const openAlertCallback = useCallback(() => {
    if (alertState.options?.onConfirmPressed) {
      alertState.options.onConfirmPressed();
    }
    closeAlertCallback();
  }, [alertState.options, closeAlertCallback]);

  useReduxDevToolsExtension(navigationRef);

  const RebookCheck = useCallback(() => {
    if (!passengers && !AA_ENABLED && !EVENTS_ENABLED && !user) {
      return <AuthStack />;
    }
    return <RootNavigator />;
  }, [user, passengers]);

  // we don't mount the Navigation Container until we run the startup logic in StartupScreen.
  // Also we will render a loading indicator since the logic is async
  const appNavigator = !hasToken ? (
    <StartupScreen />
  ) : (
    <NavigationContainer ref={navigationRef}>
      <RebookCheck />
    </NavigationContainer>
  );

  return (
    <>
      {appNavigator}
      <LoadingIndicator />
      <AwesomeAlert
        show={alertState.showAlert}
        showProgress={false}
        title={alertState.options?.title}
        message={alertState.options?.message}
        closeOnTouchOutside={false}
        messageStyle={styles.alertTextStyle}
        titleStyle={styles.alertTextStyle}
        closeOnHardwareBackPress={false}
        showCancelButton={!!alertState.options?.cancelText}
        showConfirmButton={true}
        cancelText={alertState.options?.cancelText}
        cancelButtonColor={Color.gray}
        cancelButtonStyle={styles.alertButtonStyle}
        cancelButtonTextStyle={styles.alertButtonText}
        confirmText={alertState.options?.confirmText || t('Okay')}
        confirmButtonColor={Color.primary}
        confirmButtonStyle={styles.alertButtonStyle}
        confirmButtonTextStyle={styles.alertButtonText}
        onCancelPressed={closeAlertCallback}
        onConfirmPressed={openAlertCallback}
        showXMark={alertState.options?.showXMark}
      />
    </>
  );
};
const styles = StyleSheet.create({
  alertTextStyle: {
    textAlign: 'center',
    ...CommonProperties.fontFamily,
  },
  alertButtonText: {
    ...CommonProperties.h3,
    ...CommonProperties.fontFamily,
  },
  alertButtonStyle: {
    ...CommonProperties.px5,
    ...CommonProperties.py3,
  },
});
